import { FC, ReactNode, useCallback, useEffect } from "react";

import { Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  browserDefaultTimezone,
  isDafEligibleOrganization,
  FormType,
  Locales,
  TicketingFormCategory,
} from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../../common/constants/amplitude";
import { useCurrentUserContext } from "../../../../../../common/contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../../../common/contexts/LocaleContext";
import { Trans, useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../../common/amplitude/useLiveFormAmplitudeEvents";
import { CollaboratorsBlock } from "../CollaboratorsBlock/CollaboratorsBlock";
import { FundDesignationCard } from "../FundDesignationCard/FundDesignationCard";
import { NotificationEmailsBlock } from "../NotificationEmailsBlock/NotificationEmailsBlock";
import { AdvancedOptionsTranslationsCard } from "../Translations/AdvancedOptionsTranslationsCard";
import type { StepAdvancedSettingsFormData } from "../types";

import { DisplayOnOrgPageBlock } from "./components/DisplayOnOrgPageBlock/DisplayOnOrgPageBlock";

import { OrganizationPageInfoOutput } from "@/common/types/trpc";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardDateTimePicker } from "@/dashboard-design-system/DateTimePicker";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";
import { DashboardTimezonePicker } from "@/dashboard-design-system/TimezonePicker/DashboardTimezonePicker";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { AddTargetCard } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/AddTargetCard";
import { OfflinePaymentMethodCard } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/OfflinePaymentMethodCard";

const sectionSx = (theme: Theme) => ({
  backgroundColor: theme.palette.surface.neutral.supershy,
  borderRadius: 1,
  p: 3,
});

const sectionOptionsSx = (theme: Theme) => ({
  "& > *": {
    border: `1px solid ${theme.palette.border.neutral.quiet}`,
  },
});

interface AdvancedOptionsProps {
  form: UseFormReturn<StepAdvancedSettingsFormData>;
  formType: FormType;
  formId: string;
  locale: Locales;
  /** When provided (e.g. from Donation step), used instead of context. Ticketing keeps using context. */
  organizationPageInfo?: OrganizationPageInfoOutput | null;
  /** When true, shows a skeleton for the org page block to avoid layout glitch when query resolves. */
  isOrganizationPageInfoLoading?: boolean;
  /** Passed by Ticketing step only; avoids reading occurrences from union-typed apiFormObject. */
  hasAnyOccurrencesAndHasAnOrgPage?: boolean;
  /** Passed by Ticketing step only. */
  hasNotAnyOccurrencesAndHasAnOrgPage?: boolean;
  hasNoOccurrences?: boolean;
  ticketingTimezone?: string;
  afterDisplaySection?: ReactNode;
}

export const AdvancedOptions: FC<AdvancedOptionsProps> = ({
  form,
  formType,
  formId,
  locale,
  organizationPageInfo,
  isOrganizationPageInfoLoading,
  hasAnyOccurrencesAndHasAnOrgPage: hasAnyOccurrencesAndHasAnOrgPageProp,
  hasNotAnyOccurrencesAndHasAnOrgPage: hasNotAnyOccurrencesAndHasAnOrgPageProp,
  hasNoOccurrences,
  ticketingTimezone,
  afterDisplaySection,
}) => {
  const { t } = useTranslate();
  const { locale: appLocale } = useLocaleContext();
  const { organization } = useCurrentUserContext();
  const { formCategory } = useLiveFormEditorContext();

  const hasOrgPage = Boolean(organizationPageInfo?.url);
  const hasAnyOccurrencesAndHasAnOrgPage =
    formType === FormType.Ticketing ? Boolean(hasAnyOccurrencesAndHasAnOrgPageProp) : false;
  const hasNotAnyOccurrencesAndHasAnOrgPage =
    formType === FormType.Ticketing ? Boolean(hasNotAnyOccurrencesAndHasAnOrgPageProp) : false;
  const showSupportFormBlock =
    (formType === FormType.Ticketing && hasNotAnyOccurrencesAndHasAnOrgPage) ||
    (formType === FormType.DonationForm && hasOrgPage);

  const supportForms = organizationPageInfo?.supportForms ?? [];
  const hasThreeSupportForms = supportForms.length >= 3;

  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const handleDisplayOnOrgPageChanged = useCallback(
    (params: { new_value: "Display" | "Hide"; replace_existing_campaign: boolean }) => {
      logUserActionEvent(AmplitudeEvents.DisplayOnOrgPageChanged, {
        new_value: params.new_value,
        replace_existing_campaign: params.replace_existing_campaign,
        campaign_type: formCategory,
        has_date: hasAnyOccurrencesAndHasAnOrgPage,
      });
    },
    [logUserActionEvent, formCategory, hasAnyOccurrencesAndHasAnOrgPage]
  );

  const {
    control,
    formState: { errors },
  } = form;

  const showTargetCard = [TicketingFormCategory.Event, TicketingFormCategory.Lottery].includes(
    formCategory as TicketingFormCategory
  );

  const isAuction = formCategory === TicketingFormCategory.Auction;
  const showSalesOpenAndCloseDateBlock = formType === FormType.Ticketing && Boolean(hasNoOccurrences) && !isAuction;

  const showInHonour = formType === FormType.DonationForm;
  const showDonorList = formType === FormType.DonationForm;
  const showBidHistory = formType === FormType.Ticketing && formCategory === TicketingFormCategory.Auction;
  const showOrgPageLoading =
    Boolean(isOrganizationPageInfoLoading) && (formType === FormType.Ticketing || formType === FormType.DonationForm);
  const showOrgPageBlock =
    organizationPageInfo?.isPublished === true && (hasAnyOccurrencesAndHasAnOrgPage || showSupportFormBlock);
  const hasDisplayContent =
    showInHonour ||
    showDonorList ||
    showSalesOpenAndCloseDateBlock ||
    showBidHistory ||
    showTargetCard ||
    showOrgPageLoading ||
    Boolean(showOrgPageBlock);

  const showTranslations = Boolean(organization?.canTranslateForms);
  const showTimezone = formType === FormType.Ticketing;
  const hasRegionalContent = showTranslations || showTimezone;
  const isSalesOpenAndCloseDateToggleOn = useWatch({ control, name: "hasSalesOpenAndCloseDate" }) ?? false;
  const salesOpenDateValue = useWatch({ control, name: "salesOpenDate" });
  const salesCloseDateValue = useWatch({ control, name: "salesClosedDate" });

  const atLeastOneSalesDate = () => {
    if (!salesOpenDateValue && !salesCloseDateValue) {
      return t("dashboard", "ticketing.salesAtLeastOneDateRequired") as string;
    }
    return true;
  };

  useEffect(() => {
    if (salesOpenDateValue || salesCloseDateValue) {
      void form.trigger(["salesOpenDate", "salesClosedDate"]);
    }
  }, [salesCloseDateValue, salesOpenDateValue, form]);

  return (
    <Stack gap={2}>
      <CollaboratorsBlock formId={formId} formType={formType} />

      <NotificationEmailsBlock form={form} formType={formType} />

      <Stack gap={2} sx={sectionSx}>
        <DashboardTypography variant="subtitle1">
          {t("dashboard", "step.advancedSettings.financeSection")}
        </DashboardTypography>
        <Stack gap={2} sx={sectionOptionsSx}>
          <OfflinePaymentMethodCard form={form} />
          {formType === FormType.DonationForm && organization && isDafEligibleOrganization(organization) && (
            <LiveEditorBlock>
              <LiveEditorToggle
                name="dafEnabled"
                control={control}
                labelVariant="body1"
                data-test="daf-enabled"
                onValueChange={(checked) =>
                  logUserActionEvent(AmplitudeEvents.FormDafPaymentChanged, { dafEnabled: checked })
                }
                label={t("dashboard", "daf.activationLabel")}
                tooltip={t("dashboard", "daf.activationTooltip")}
              />
            </LiveEditorBlock>
          )}
          <FundDesignationCard form={form} formId={formId} formType={formType} locale={locale} />
        </Stack>
      </Stack>

      {hasDisplayContent && (
        <Stack gap={2} sx={sectionSx}>
          <DashboardTypography variant="subtitle1">
            {t("dashboard", "step.advancedSettings.displaySection")}
          </DashboardTypography>

          <Stack gap={2} sx={sectionOptionsSx}>
            {showInHonour && (
              <LiveEditorBlock>
                <LiveEditorToggle
                  label={<Trans space="dashboard" i18nKey="donationForm.allowInHonour" components={{ i: <i /> }} />}
                  labelVariant="body1"
                  description={t("dashboard", "donationForm.allowInHonourDescription")}
                  name="allowInHonour"
                  control={control}
                  onValueChange={(checked) =>
                    logUserActionEvent(checked ? AmplitudeEvents.InHonourAdded : AmplitudeEvents.InHonourRemoved)
                  }
                />
              </LiveEditorBlock>
            )}

            {showDonorList && (
              <LiveEditorBlock>
                <LiveEditorToggle
                  label={t("dashboard", "donationForm.displayDonorList")}
                  labelVariant="body1"
                  name="displayDonorList"
                  control={control}
                  onValueChange={(checked) =>
                    logUserActionEvent(checked ? AmplitudeEvents.DonorListAdded : AmplitudeEvents.DonorListRemoved)
                  }
                />
              </LiveEditorBlock>
            )}

            {showSalesOpenAndCloseDateBlock && (
              <LiveEditorBlock>
                <LiveEditorToggle
                  label={t("dashboard", "ticketing.salesOpenAndCloseDateToggle")}
                  labelVariant="body1"
                  name="hasSalesOpenAndCloseDate"
                  control={control}
                  data-test="advanced-options-sales-open-close-toggle"
                />
                {isSalesOpenAndCloseDateToggleOn && (
                  <Stack gap={2} sx={{ pt: 1 }}>
                    <DashboardDateTimePicker
                      name="salesOpenDate"
                      label={t("dashboard", "ticketing.salesOpenDateLabel")}
                      control={control}
                      locale={appLocale}
                      size="small"
                      rules={[
                        {
                          validate: {
                            atLeastOne: atLeastOneSalesDate,
                            isBeforeClose: (value?: Date | null) => {
                              if (!value || !salesCloseDateValue) {
                                return true;
                              }
                              return new Date(value) < new Date(salesCloseDateValue)
                                ? true
                                : (t("dashboard", "ticketing.salesOpenAfterCloseError") as string);
                            },
                          },
                        },
                      ]}
                      errorMessage={errors.salesOpenDate && (errors.salesOpenDate.message as string)}
                      data-test="advanced-options-sales-open-date"
                      timezone={ticketingTimezone || browserDefaultTimezone}
                    />
                    <DashboardDateTimePicker
                      name="salesClosedDate"
                      label={t("dashboard", "ticketing.salesCloseDateLabel")}
                      control={control}
                      locale={appLocale}
                      size="small"
                      rules={[{ validate: { atLeastOne: atLeastOneSalesDate } }]}
                      errorMessage={errors.salesClosedDate && (errors.salesClosedDate.message as string)}
                      data-test="advanced-options-sales-close-date"
                      timezone={ticketingTimezone || browserDefaultTimezone}
                    />
                    {salesOpenDateValue && new Date(salesOpenDateValue) < new Date() && (
                      <DashboardTypography variant="caption" vibe="text-warning-quiet">
                        {t("dashboard", "ticketing.salesOpenInPastWarning")}
                      </DashboardTypography>
                    )}
                  </Stack>
                )}
              </LiveEditorBlock>
            )}

            {showBidHistory && (
              <LiveEditorBlock>
                <LiveEditorToggle
                  label={t("dashboard", "ticketing.displayBidHistory")}
                  labelVariant="body1"
                  name="displayBidHistory"
                  control={control}
                />
              </LiveEditorBlock>
            )}

            {showTargetCard && <AddTargetCard form={form} />}

            {showOrgPageLoading ? (
              <LiveEditorBlock>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <DashboardSkeleton variant="text" width={220} height={24} />
                  <DashboardSkeleton variant="rounded" width={44} height={24} sx={{ borderRadius: 12 }} />
                </Stack>
              </LiveEditorBlock>
            ) : (
              showOrgPageBlock && (
                <DisplayOnOrgPageBlock
                  form={form}
                  control={control}
                  formId={formId}
                  isTicketingWithDate={hasAnyOccurrencesAndHasAnOrgPage}
                  orgPageUrl={organizationPageInfo?.url}
                  hasThreeSupportForms={hasThreeSupportForms}
                  supportForms={supportForms}
                  locale={locale}
                  onDisplayOnOrgPageChanged={handleDisplayOnOrgPageChanged}
                />
              )
            )}
          </Stack>
        </Stack>
      )}

      {afterDisplaySection}

      {hasRegionalContent && (
        <Stack gap={2} sx={sectionSx}>
          <DashboardTypography variant="subtitle1">
            {t("dashboard", "step.advancedSettings.regionalSection")}
          </DashboardTypography>

          <Stack gap={2} sx={sectionOptionsSx}>
            {showTimezone && (
              <LiveEditorBlock gap={1}>
                <DashboardTypography variant="body1" vibe="text-neutral-moderate">
                  {t("dashboard", "common.timezone")}
                </DashboardTypography>
                <DashboardTimezonePicker
                  control={control}
                  name="timezone"
                  defaultTimezone={form.watch("timezone") || browserDefaultTimezone}
                />
              </LiveEditorBlock>
            )}

            {showTranslations && <AdvancedOptionsTranslationsCard formType={formType} />}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
